import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { GetStoredInsightsRequestInputBodyDto, StoredInsightsResponseDto } from '@malou-io/package-dto';
import {
    AggregationTimeScale,
    isNotNil,
    MalouComparisonPeriod,
    MalouMetric,
    PlatformFilterPage,
    PlatformKey,
    StatisticsType,
} from '@malou-io/package-utils';

import { Restaurant } from '#modules/restaurants/models/restaurant';
import { RestaurantStore } from '#modules/restaurants/stores/restaurant.store';
import { CommunityInsightsChartData } from '#modules/statistics-social-networks/models/community-insight-chart-data';
import { CommunityChartData } from '#modules/statistics-social-networks/models/social-networks.interfaces';
import { getInsightsErrorText } from '#modules/statistics/models/insights';
import { InsightsApiService } from '#modules/statistics/services/insights.api.service';
import { StatisticsFiltersStore } from '#modules/statistics/stores/store/statistics-filters-store/models/statistics-filters-store.model';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { ViewBy } from '#shared/enums/view-by.enum';
import { getChartViewByFromDates, getDayMonthYearFromDate, isDateSetOrGenericPeriod } from '#shared/helpers/index';
import { SocialNetworkInsights } from '#shared/interfaces/social-network-insights.interface';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

@Injectable()
export class SocialNetworksCommunityInsightsContext {
    private readonly _insightsApiService = inject(InsightsApiService);
    private readonly _translate = inject(TranslateService);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _statisticsStore = inject(StatisticsStore);
    private readonly _restaurantStore = inject(RestaurantStore);

    readonly statisticsFilters$ = toObservable(this._statisticsStore.filters);
    readonly selectedRestaurant$ = toObservable(this._restaurantStore.restaurant).pipe(
        distinctUntilChanged((prev, curr) => prev?._id === curr?._id)
    );

    readonly httpError: WritableSignal<string | null> = signal(null);
    readonly isLoading = signal(true);
    readonly insightsError: WritableSignal<string | null> = signal(null);
    readonly areAllPlatformsInError: WritableSignal<boolean> = signal(false);
    readonly platformsErrorTooltip: WritableSignal<string | null> = signal(null);
    readonly hasDate: WritableSignal<boolean> = signal(false);

    readonly dailyInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);
    readonly dailyPreviousInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);
    readonly weeklyInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);
    readonly weeklyPreviousInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);
    readonly monthlyInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);
    readonly monthlyPreviousInsightsChartData = signal<CommunityInsightsChartData>({} as CommunityInsightsChartData);

    readonly dailyStoredInsightsResponseDtoData = signal<StoredInsightsResponseDto>({} as StoredInsightsResponseDto);
    readonly dailyPreviousStoredInsightsResponseDtoData = signal<StoredInsightsResponseDto>({} as StoredInsightsResponseDto);

    readonly startDate: WritableSignal<string | null> = signal(null);
    readonly endDate: WritableSignal<string | null> = signal(null);

    readonly viewBy: WritableSignal<ViewBy | null> = signal(null);

    readonly chartData$ = combineLatest([this.selectedRestaurant$, this.statisticsFilters$]).pipe(
        filter(
            ([restaurant, statisticsFilters]: [Restaurant, StatisticsFiltersStore]) =>
                !!restaurant &&
                isNotNil(statisticsFilters.dates.startDate) &&
                isNotNil(statisticsFilters.dates.endDate) &&
                isDateSetOrGenericPeriod(statisticsFilters.dates)
        ),
        map(([restaurant, statisticsFilters]: [Restaurant, StatisticsFiltersStore]) => [
            restaurant,
            {
                startDate: statisticsFilters.dates.startDate,
                endDate: statisticsFilters.dates.endDate,
            },
            statisticsFilters.platforms[PlatformFilterPage.SOCIAL_NETWORKS],
            statisticsFilters.comparisonPeriod,
        ]),
        tap(() => this._reset()),
        switchMap(
            ([restaurant, { startDate, endDate }, platformKeys, comparisonPeriod]: [
                Restaurant,
                { startDate: Date; endDate: Date },
                PlatformKey[],
                MalouComparisonPeriod,
            ]) => {
                const requestBody: GetStoredInsightsRequestInputBodyDto = {
                    restaurantIds: [restaurant._id],
                    platformKeys,
                    startDate: getDayMonthYearFromDate(startDate),
                    endDate: getDayMonthYearFromDate(endDate),
                    metrics: [MalouMetric.FOLLOWERS],
                };
                return forkJoin([
                    this._insightsApiService.getStoredInsights({ ...requestBody, statisticsType: StatisticsType.SOCIAL }),
                    this._insightsApiService.getStoredInsights({ ...requestBody, comparisonPeriod, statisticsType: StatisticsType.SOCIAL }),
                    of(restaurant._id),
                    of(platformKeys),
                    of(comparisonPeriod),
                    of({ startDate, endDate }),
                ]);
            }
        ),
        filter(([current]) => !!current),
        map(([currentInsights, previousInsights, restaurantId, platformKeys, comparisonPeriod, { startDate, endDate }]) => {
            const currentRestaurantInsights = currentInsights.data?.[restaurantId];
            const previousRestaurantInsights = previousInsights.data?.[restaurantId];

            const platformsInError = this._getPlatformsInError(currentInsights.data, restaurantId, platformKeys);
            if (platformsInError.length) {
                this.platformsErrorTooltip.set(this._getPlatformsErrorTooltip(platformsInError));
            }
            if (platformKeys.length === platformsInError.length) {
                this.areAllPlatformsInError.set(true);
                this._setInsightsError(currentRestaurantInsights?.[PlatformKey.FACEBOOK]?.malouErrorCode);
                this.hasDate.set(false);
            } else {
                platformKeys.forEach((platform) => {
                    if (platformsInError.includes(platform)) {
                        if (!currentRestaurantInsights?.[platform]?.hasData) {
                            delete currentRestaurantInsights?.[platform];
                        }
                        if (!previousRestaurantInsights?.[platform]?.hasData) {
                            delete previousRestaurantInsights?.[platform];
                        }
                    }
                });

                this._setActionStoreData({
                    [PlatformKey.FACEBOOK]: currentRestaurantInsights?.[PlatformKey.FACEBOOK]?.insights,
                    [PlatformKey.INSTAGRAM]: currentRestaurantInsights?.[PlatformKey.INSTAGRAM]?.insights,
                    [PlatformKey.TIKTOK]: currentRestaurantInsights?.[PlatformKey.TIKTOK]?.insights,
                });

                this.dailyInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(currentInsights.data, restaurantId, AggregationTimeScale.BY_DAY)
                );
                this.dailyPreviousInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(
                        previousInsights.data,
                        restaurantId,
                        AggregationTimeScale.BY_DAY,
                        comparisonPeriod
                    )
                );
                this.weeklyInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(currentInsights.data, restaurantId, AggregationTimeScale.BY_WEEK)
                );
                this.weeklyPreviousInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(
                        previousInsights.data,
                        restaurantId,
                        AggregationTimeScale.BY_WEEK,
                        comparisonPeriod
                    )
                );
                this.monthlyInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(currentInsights.data, restaurantId, AggregationTimeScale.BY_MONTH)
                );
                this.monthlyPreviousInsightsChartData.set(
                    this._getInsightsChartDataByTimeScale(
                        previousInsights.data,
                        restaurantId,
                        AggregationTimeScale.BY_MONTH,
                        comparisonPeriod
                    )
                );

                this.viewBy.set(getChartViewByFromDates(startDate, endDate));
                this.startDate.set(DateTime.fromObject(currentInsights.data.startDate).toISODate());
                this.endDate.set(DateTime.fromObject(currentInsights.data.endDate).toISODate());
                this.dailyStoredInsightsResponseDtoData.set(currentInsights.data);
                this.dailyPreviousStoredInsightsResponseDtoData.set(previousInsights.data);
            }
            this.isLoading.set(false);
        }),
        catchError((error) => {
            this.httpError.set(error);
            this.hasDate.set(false);
            this.isLoading.set(false);
            return of(EMPTY);
        }),
        takeUntilDestroyed(this._destroyRef)
    );

    private _reset(): void {
        this.httpError.set(null);
        this.insightsError.set(null);
        this.isLoading.set(true);
        this.platformsErrorTooltip.set(null);
        this.areAllPlatformsInError.set(false);
    }

    private _setInsightsError(message?: string): void {
        this.insightsError.set(
            this._translate.instant(getInsightsErrorText(message), {
                platformName: this._enumTranslatePipe.transform(PlatformKey.FACEBOOK, 'platform_key'),
            })
        );
    }

    private _getPlatformsInError(
        storedInsights: StoredInsightsResponseDto,
        restaurantId: string,
        filteredPlatforms: PlatformKey[]
    ): PlatformKey[] {
        return filteredPlatforms.filter((platform) => storedInsights?.[restaurantId]?.[platform]?.malouErrorCode);
    }

    private _getPlatformsErrorTooltip(platformsInError: PlatformKey[]): string {
        return platformsInError.map((platform) => this._enumTranslatePipe.transform(platform, 'platform_key')).join(', ');
    }

    private _getInsightsChartDataByTimeScale(
        data: SocialNetworkInsights,
        restaurantId: string,
        aggregationTimeScale: AggregationTimeScale,
        comparisonPeriod?: MalouComparisonPeriod
    ): CommunityInsightsChartData {
        let startDate = data.startDate;
        let endDate = data.endDate;
        if (comparisonPeriod && comparisonPeriod !== MalouComparisonPeriod.PREVIOUS_PERIOD) {
            startDate = data[restaurantId]!.dateFilters!.startDate;
            endDate = data[restaurantId]!.dateFilters!.endDate;
        }
        return new CommunityInsightsChartData({
            data: {
                [PlatformKey.FACEBOOK]: data[restaurantId]?.[PlatformKey.FACEBOOK]?.insights,
                [PlatformKey.INSTAGRAM]: data[restaurantId]?.[PlatformKey.INSTAGRAM]?.insights,
                [PlatformKey.TIKTOK]: data[restaurantId]?.[PlatformKey.TIKTOK]?.insights,
            },
            startDate,
            endDate,
            aggregationTimeScale,
            comparisonPeriod,
        });
    }

    private _setActionStoreData(data: CommunityChartData): void {
        this._statisticsStore.editFollowersRawDataV2(data);
    }
}
